import { Badge, Card, Metric } from '@evogard/ui';
import { ZERO, count, fromColumn, salesTargetProgressOf } from '@evogard/domain';
import type { SalesTarget } from '@evogard/contract';
import { MoneyValue } from './common.js';

/**
 * A própria meta de um consultor, como ele a vê.
 *
 * ## Por que um cartão, e não a linha da tabela
 *
 * A tabela responde "como está cada um dos quarenta", e é a leitura de quem
 * conduz. Quem tem só `sales.targets:ver` enxerga **uma** meta — a dele —, e
 * uma tabela de uma linha é um cabeçalho gasto para dizer dois números. O
 * cartão diz os mesmos dois números e a proporção entre eles.
 *
 * ## Ele não diz "bateu", e o anel não é um veredito
 *
 * **A ressalva escrita saiu do cartão em 11/09, por decisão do dono, e o que
 * ela guardava continua guardado pelo desenho** — não há verde, não há
 * "atingida", e a cor do anel é a mesma em 3% e em 300%. Ela dizia "nenhum
 * número aqui deve ser lido como 'bateu' ou 'não bateu'", e repetia num cartão
 * de uma linha o que o cartão de motivos acima já diz quando há o que dizer.
 * Quem for reescrever a copy daqui não precisa trazê-la de volta; o que não
 * pode voltar é o **estado** — um selo de meta atingida.
 *
 * O anel mostra a **razão**, que é aritmética. Meta zero é meta sempre atingida
 * (§5.3) — e por isso `null` de `salesTargetProgressOf` desenha os números
 * **sem** anel, em vez de uma volta cheia que ninguém preencheu.
 *
 * Quem calcula a razão é `@evogard/domain`, e não este arquivo: dividir aqui
 * seria a segunda implementação da mesma conta, e é onde o `Infinity` da meta
 * zero apareceria — na tela, e não num teste.
 *
 * ## A superfície é a do `Card`, e a primeira versão errou isso
 *
 * Ela usava os tokens do `chrome` — o navy da marca —, escolhidos pela cor de
 * uma referência de estilo em vez da função deles. **`chrome` é deliberadamente
 * escuro nos DOIS temas**: é a cor do rail, e ela não acompanha claro e escuro
 * porque não deve acompanhar. Num cartão de conteúdo isso vira uma ilha escura
 * no tema claro, e um bloco que ignora o tema do resto da página.
 *
 * A segunda versão copiou as quatro classes do cartão de filtros
 * (`rounded-grande border-borda bg-superficie shadow-cartao`) — e copiar é o
 * defeito de que a padronização de 17/09 tirou os sete cartões. Agora é o
 * `Card`, com `bleed`: a régua de 3px do topo precisa encostar nas bordas, e é
 * a sangria que a deixa encostar. O que veio da referência de estilo foi a
 * **forma** — a régua, o valor grande à esquerda, o anel à direita e a pílula
 * com a meta.
 */
export function TargetCard({ target }: { target: SalesTarget }) {
  const measured = target.attainment.estado === 'medido' ? target.attainment : null;
  const progress =
    measured === null
      ? null
      : salesTargetProgressOf(
          { value: fromColumn(target.value), quantity: target.quantity },
          /* `?? ZERO` e não `?? 0`: dinheiro é o tipo `Money` da casa, e o zero
           * literal seria um `number` solto entrando na conta — o começo do
           * `Number(linha.valor)` que funciona em todo teste e falha na
           * conciliação do mês. */
          { quantity: measured.quantity, value: fromColumn(measured.value) ?? ZERO },
        );

  return (
    <Card bleed>
      {/* A régua do topo é PREENCHIMENTO, e não texto: `text-primaria-texto` solta é
          ação, e a régua do design a reprova fora dos componentes da casa. */}
      <div className="h-[3px] bg-primaria" />

      <div className="flex flex-wrap items-center justify-between gap-6 p-4">
        <div className="flex min-w-0 flex-col gap-1">
          <h3 className="text-[0.8125rem] font-bold uppercase text-texto">
            {target.target.nome}
          </h3>

          {/*
           * `Metric` e não um número escrito à mão: o tamanho da escala da marca
           * é dele, e a régua do design reprova `text-2xl` para cima fora dos
           * componentes da casa. A primeira versão deste cartão trazia um
           * `text-3xl` da referência de estilo — o clichê do número grande com
           * rótulo pequeno, que o `DESIGN.md` bane e que num cartão de
           * remuneração seria o pior lugar para exibir um número.
           *
           * `value` **nunca é `undefined` aqui**: o esqueleto de `Metric` diz
           * "ainda não sei", e quem diz isso nesta tela é o estado
           * `nao_apuravel`, com o motivo escrito. Um esqueleto no lugar dele
           * pareceria carregamento eterno.
           */}
          <dl className="mt-3">
            <Metric
              label="Realizado"
              value={measured === null ? '—' : measured.quantity}
              support={
                measured === null ? (
                  'Não pôde ser medido — o aviso acima diz o que falta.'
                ) : (
                  <>
                    {count(measured.quantity, 'venda que conta', 'vendas que contam')} ·{' '}
                    <MoneyValue value={measured.value} />
                  </>
                )
              }
            />
          </dl>
        </div>

        <div className="flex shrink-0 flex-col items-center gap-2">
          <TargetRing progress={progress} />
        </div>
      </div>
    </Card>
  );
}

/**
 * O anel, ou o lugar dele quando não há proporção.
 *
 * `null` acontece quando a meta declara zero ou não declara a dimensão — ver
 * `salesTargetProgressOf`. O vazio é dito com palavra, e não com um anel de 0%:
 * um anel vazio se lê como "não fez nada", que é uma afirmação sobre o
 * desempenho de alguém quando a causa é ninguém ter declarado a meta.
 *
 * **O arco é limitado a uma volta; o número não.** Quem fez 120% lê 120% e vê a
 * volta cheia — o contrário daria duas voltas sobrepostas, que se leem como 20%.
 *
 * `aria-hidden` no `<svg>`: ele não carrega informação que o texto não diga. O
 * percentual está no centro, e o realizado e a meta estão escritos ao lado.
 */
function TargetRing({
  progress,
}: {
  progress: ReturnType<typeof salesTargetProgressOf>;
}) {
  if (progress === null) {
    return (
      <p className="max-w-40 text-center text-xs font-medium text-texto-suave">
        A meta não declara sobre o que medir a proporção.
      </p>
    );
  }

  const RADIUS = 32;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
  const filled = Math.min(progress.ratio, 1);
  const percentage = Math.round(progress.ratio * 100);

  return (
    <>
      <div className="relative size-[70px]">
        <svg viewBox="0 0 70 70" className="size-full -rotate-90" aria-hidden="true">
          <circle
            cx="35"
            cy="35"
            r={RADIUS}
            fill="none"
            strokeWidth="6"
            className="stroke-borda"
          />
          <circle
            cx="35"
            cy="35"
            r={RADIUS}
            fill="none"
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={CIRCUMFERENCE * (1 - filled)}
            className="stroke-primaria-texto"
          />
        </svg>
        <p className="absolute inset-0 flex items-center justify-center text-xs font-bold text-texto tabular-nums">
          {percentage}%
        </p>
      </div>

      <Badge tone="neutro">
        {progress.dimension === 'quantidade'
          ? `Meta: ${progress.target}`
          : 'Meta em dinheiro'}
      </Badge>
    </>
  );
}
